'use client';

import { HStack, VStack, Box } from '@terra/ui-styled-system';
import { IconButton } from '../../components/icon-button';
import { Skeleton } from '../../components/skeleton';
import { Text } from '../../components/text';
import { Tooltip } from '../../components/tooltip';
import { HelpCircleIcon, Edit03Icon } from '@terra/ui-icons';
import type { ReactNode } from 'react';
import { automationId } from '@terra/shared-utils';
import type { SettingsRowAction } from './types';

const ACTION_BUTTON = {
  sm: { size: 'sm', h: 8, iconSize: 16, minW: 8, px: 2 },
  xs: { size: 'xs', h: 6, iconSize: 12, minW: 6, px: 1 },
} as const;

export type SettingsRowProps = {
  label: ReactNode;
  value?: ReactNode;
  valueTestId?: string;
  description?: ReactNode;
  tooltip?: string;
  leadingIcon?: ReactNode;
  action?: SettingsRowAction;
  control?: ReactNode;
  isLoading?: boolean;
  isReadOnly?: boolean;
  valueWrap?: boolean;
};
export function SettingsRow({
  label,
  value,
  valueTestId,
  description,
  tooltip,
  leadingIcon,
  action,
  control,
  isLoading,
  isReadOnly,
  valueWrap = false,
}: SettingsRowProps) {
  const resolvedAction = isReadOnly ? undefined : action;
  const actionButton = ACTION_BUTTON[resolvedAction?.size ?? 'sm'];
  const actionIcon = resolvedAction?.icon ?? (
    <Edit03Icon size={actionButton.iconSize} />
  );

  return (
    <HStack
      gap={4}
      w="full"
      justifyContent="space-between"
      alignItems={valueWrap ? 'flex-start' : 'center'}
      px={0}
      py={0}
      minH="32px"
    >
      <HStack
        gap={2}
        alignItems="center"
        minW={0}
        minH={valueWrap ? 8 : undefined}
        flex={1}
      >
        {leadingIcon ? (
          <Box display="flex" color="fg.neutral.muted" flexShrink={0}>
            {leadingIcon}
          </Box>
        ) : null}
        <VStack alignItems="flex-start" gap={0.5} minW={0}>
          <HStack gap={1} alignItems="center" minW={0}>
            {typeof label === 'string' ? (
              <Text fontSize="sm" color="fg.neutral.muted" lineClamp={1}>
                {label}
              </Text>
            ) : (
              label
            )}
            {tooltip ? (
              <Tooltip title={tooltip} positioning={{ strategy: 'fixed' }}>
                <Box display="flex" color="fg.neutral.muted">
                  <HelpCircleIcon size={16} />
                </Box>
              </Tooltip>
            ) : null}
          </HStack>
          {description ? (
            typeof description === 'string' ? (
              <Text fontSize="xs" color="fg.neutral.subtle" lineClamp={1}>
                {description}
              </Text>
            ) : (
              description
            )
          ) : null}
        </VStack>
      </HStack>
      <HStack
        gap={2}
        alignItems={valueWrap ? 'flex-start' : 'center'}
        justifyContent="flex-end"
        minW={0}
        flex={valueWrap ? 1 : undefined}
        maxW={valueWrap ? '55%' : undefined}
      >
        {isLoading ? (
          <Skeleton h="20px" w="120px" />
        ) : control ? (
          control
        ) : value !== undefined && value !== null ? (
          <SettingsRowValue
            value={value}
            valueTestId={valueTestId}
            valueWrap={valueWrap}
            hasAction={Boolean(resolvedAction)}
          />
        ) : null}
        {resolvedAction ? (
          <Tooltip
            title={resolvedAction.label}
            positioning={{ strategy: 'fixed' }}
          >
            <IconButton
              id={resolvedAction.id}
              variant="ghost"
              size={actionButton.size}
              aria-label={resolvedAction.label}
              onClick={resolvedAction.onClick}
              disabled={resolvedAction.disabled}
              h={actionButton.h}
              minW={actionButton.minW}
              px={actionButton.px}
              {...(resolvedAction.testId
                ? automationId(resolvedAction.testId)
                : {})}
            >
              {actionIcon}
            </IconButton>
          </Tooltip>
        ) : null}
      </HStack>
    </HStack>
  );
}

function SettingsRowValue({
  value,
  valueTestId,
  valueWrap,
  hasAction,
}: {
  value: ReactNode;
  valueTestId?: string;
  valueWrap: boolean;
  hasAction: boolean;
}) {
  const stringValue = typeof value === 'string' ? value : undefined;
  const valueBox = (
    <Box
      h={hasAction && !valueWrap ? 8 : undefined}
      minH={hasAction && valueWrap ? 8 : undefined}
      px={hasAction && !valueWrap ? 3 : undefined}
      display={valueWrap ? 'flex' : 'block'}
      alignItems={valueWrap ? 'center' : undefined}
      justifyContent={valueWrap ? 'flex-end' : undefined}
      fontSize="sm"
      color="fg.neutral.default"
      textAlign="right"
      lineHeight={hasAction && !valueWrap ? '32px' : undefined}
      minW={0}
      w={valueWrap ? 'full' : undefined}
      maxW={valueWrap ? 'full' : '360px'}
      overflow={valueWrap ? 'visible' : 'hidden'}
      textOverflow={valueWrap ? undefined : 'ellipsis'}
      whiteSpace={valueWrap ? 'normal' : 'nowrap'}
      wordBreak={valueWrap ? 'break-all' : undefined}
      {...(valueTestId ? automationId(valueTestId) : {})}
    >
      {value}
    </Box>
  );

  if (!stringValue || valueWrap) {
    return valueBox;
  }

  return (
    <Tooltip title={stringValue} positioning={{ strategy: 'fixed' }}>
      {valueBox}
    </Tooltip>
  );
}
